import React, { useMemo } from 'react';

const QR_VERSION = 3;
const QR_SIZE = QR_VERSION * 4 + 17;
const QR_DATA_CODEWORDS = 55;
const QR_ECC_CODEWORDS = 15;

const getQrFormatBits = (maskPattern: number): number => {
  const formatData = (1 << 3) | maskPattern;
  let bits = formatData << 10;
  const generator = 0b10100110111;
  for (let i = 14; i >= 10; i -= 1) {
    if (((bits >> i) & 1) !== 0) {
      bits ^= generator << (i - 10);
    }
  }
  return ((formatData << 10) | bits) ^ 0b101010000010010;
};

const multiplyQrField = (left: number, right: number): number => {
  let result = 0;
  let a = left;
  let b = right;
  while (b > 0) {
    if ((b & 1) !== 0) result ^= a;
    a <<= 1;
    if ((a & 0x100) !== 0) a ^= 0x11d;
    b >>= 1;
  }
  return result;
};

const powerQrField = (value: number, exponent: number): number => {
  let result = 1;
  for (let i = 0; i < exponent; i += 1) {
    result = multiplyQrField(result, value);
  }
  return result;
};

const buildQrGeneratorPolynomial = (degree: number): number[] => {
  let polynomial = [1];
  for (let i = 0; i < degree; i += 1) {
    const root = powerQrField(2, i);
    const next = new Array(polynomial.length + 1).fill(0);
    polynomial.forEach((coefficient, index) => {
      next[index] ^= multiplyQrField(coefficient, root);
      next[index + 1] ^= coefficient;
    });
    polynomial = next;
  }
  return polynomial;
};

const appendQrBits = (bits: number[], value: number, length: number): void => {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >> i) & 1);
  }
};

const encodeUtf8Bytes = (value: string): number[] => {
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(value));
  }

  return Array.from(unescape(encodeURIComponent(value))).map((char) => char.charCodeAt(0));
};

const encodeQrPayload = (value: string): number[] => {
  const bytes = encodeUtf8Bytes(value);
  if (bytes.length > QR_DATA_CODEWORDS - 3) {
    throw new Error('QR route target is too long for the workshop QR renderer.');
  }

  const bits: number[] = [];
  appendQrBits(bits, 0b0100, 4);
  appendQrBits(bits, bytes.length, 8);
  bytes.forEach((byte) => appendQrBits(bits, byte, 8));
  const capacityBits = QR_DATA_CODEWORDS * 8;
  appendQrBits(bits, 0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    data.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  const pads = [0xec, 0x11];
  let padIndex = 0;
  while (data.length < QR_DATA_CODEWORDS) {
    data.push(pads[padIndex % pads.length]);
    padIndex += 1;
  }
  return data;
};

const buildQrErrorCorrection = (data: number[]): number[] => {
  const generator = buildQrGeneratorPolynomial(QR_ECC_CODEWORDS);
  const remainder = new Array(QR_ECC_CODEWORDS).fill(0);
  data.forEach((byte) => {
    const factor = byte ^ remainder.shift();
    remainder.push(0);
    for (let i = 0; i < QR_ECC_CODEWORDS; i += 1) {
      remainder[i] ^= multiplyQrField(generator[i], factor);
    }
  });
  return remainder;
};

const reserveQrModule = (
  modules: boolean[][],
  reserved: boolean[][],
  x: number,
  y: number,
  value: boolean
): void => {
  if (x < 0 || y < 0 || x >= QR_SIZE || y >= QR_SIZE) return;
  modules[y][x] = value;
  reserved[y][x] = true;
};

const drawQrFinder = (
  modules: boolean[][],
  reserved: boolean[][],
  startX: number,
  startY: number
): void => {
  for (let y = -1; y <= 7; y += 1) {
    for (let x = -1; x <= 7; x += 1) {
      const absoluteX = startX + x;
      const absoluteY = startY + y;
      const isFinder =
        x >= 0 &&
        y >= 0 &&
        x <= 6 &&
        y <= 6 &&
        (x === 0 || y === 0 || x === 6 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4));
      reserveQrModule(modules, reserved, absoluteX, absoluteY, isFinder);
    }
  }
};

const drawQrAlignment = (
  modules: boolean[][],
  reserved: boolean[][],
  centerX: number,
  centerY: number
): void => {
  for (let y = -2; y <= 2; y += 1) {
    for (let x = -2; x <= 2; x += 1) {
      reserveQrModule(
        modules,
        reserved,
        centerX + x,
        centerY + y,
        Math.max(Math.abs(x), Math.abs(y)) !== 1
      );
    }
  }
};

const drawQrFunctionPatterns = (modules: boolean[][], reserved: boolean[][]): void => {
  drawQrFinder(modules, reserved, 0, 0);
  drawQrFinder(modules, reserved, QR_SIZE - 7, 0);
  drawQrFinder(modules, reserved, 0, QR_SIZE - 7);
  drawQrAlignment(modules, reserved, 22, 22);

  for (let i = 8; i < QR_SIZE - 8; i += 1) {
    reserveQrModule(modules, reserved, i, 6, i % 2 === 0);
    reserveQrModule(modules, reserved, 6, i, i % 2 === 0);
  }
  reserveQrModule(modules, reserved, 8, QR_SIZE - 8, true);
};

const drawQrFormat = (modules: boolean[][], reserved: boolean[][]): void => {
  const bits = getQrFormatBits(0);
  const firstPositions = [
    [8, 0],
    [8, 1],
    [8, 2],
    [8, 3],
    [8, 4],
    [8, 5],
    [8, 7],
    [8, 8],
    [7, 8],
    [5, 8],
    [4, 8],
    [3, 8],
    [2, 8],
    [1, 8],
    [0, 8],
  ];
  const secondPositions = [
    [QR_SIZE - 1, 8],
    [QR_SIZE - 2, 8],
    [QR_SIZE - 3, 8],
    [QR_SIZE - 4, 8],
    [QR_SIZE - 5, 8],
    [QR_SIZE - 6, 8],
    [QR_SIZE - 7, 8],
    [8, QR_SIZE - 8],
    [8, QR_SIZE - 7],
    [8, QR_SIZE - 6],
    [8, QR_SIZE - 5],
    [8, QR_SIZE - 4],
    [8, QR_SIZE - 3],
    [8, QR_SIZE - 2],
    [8, QR_SIZE - 1],
  ];
  firstPositions.forEach(([x, y], index) =>
    reserveQrModule(modules, reserved, x, y, ((bits >> index) & 1) !== 0)
  );
  secondPositions.forEach(([x, y], index) =>
    reserveQrModule(modules, reserved, x, y, ((bits >> index) & 1) !== 0)
  );
};

const buildQrModules = (value: string): boolean[][] => {
  const modules = Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false));
  const reserved = Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false));
  drawQrFunctionPatterns(modules, reserved);

  const data = encodeQrPayload(value);
  const bits = [...data, ...buildQrErrorCorrection(data)].flatMap((codeword) =>
    Array.from({ length: 8 }, (_, index) => (codeword >> (7 - index)) & 1)
  );
  let bitIndex = 0;
  let direction = -1;
  let y = QR_SIZE - 1;

  for (let rightX = QR_SIZE - 1; rightX >= 1; rightX -= 2) {
    if (rightX === 6) rightX -= 1;
    for (;;) {
      for (let i = 0; i < 2; i += 1) {
        const x = rightX - i;
        if (!reserved[y][x]) {
          const mask = (x + y) % 2 === 0;
          modules[y][x] = Boolean((bits[bitIndex] || 0) ^ (mask ? 1 : 0));
          bitIndex += 1;
        }
      }
      y += direction;
      if (y < 0 || y >= QR_SIZE) {
        y -= direction;
        direction *= -1;
        break;
      }
    }
  }

  drawQrFormat(modules, reserved);
  return modules;
};

const WorkshopQrCode: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const modules = useMemo(() => buildQrModules(value), [value]);
  const quietZone = 4;
  const size = QR_SIZE + quietZone * 2;

  return (
    <svg
      role="img"
      aria-label="Kod QR prowadzący do sprawy"
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      shapeRendering="crispEdges"
      data-qr-value={value}
    >
      <rect width={size} height={size} fill="#f7faf7" rx="1.5" />
      {modules.flatMap((row, moduleY) =>
        row.map((isDark, moduleX) =>
          isDark ? (
            <rect
              key={`${moduleX}-${moduleY}`}
              x={moduleX + quietZone}
              y={moduleY + quietZone}
              width="1"
              height="1"
              fill="#10231b"
            />
          ) : null
        )
      )}
    </svg>
  );
};

export default WorkshopQrCode;
